<template>
  <div class="d-comments-panel">
    <h4
      v-if="displayHeading"
      class="h2 space-bottom-2em"
    >
      <span v-if="!isInitialized || !numComments">Comments</span>
      <span v-else>
        {{ numComments }} {{ numComments | duiPluralize('Comment', 'Comments') }}
      </span>
    </h4>

    <div>
      <DCommentBox
        v-if="isUserSignedIn"
        class="new-comment"
        :item-slug="itemSlug"
        :item-type="itemType"
        :user-avatar-url="userAvatarUrl"
        :user-name="userName"
        @comment-added="commentAdded"
      />

      <DCommentBoxGuest v-else />

      <div
        v-if="comments.length"
        class="comments-container border-top"
      >
        <DCommentsGroup
          v-for="comment in comments"
          :key="comment.id"
          :comment="comment"
          :is-user-signed-in="isUserSignedIn"
          :item-slug="itemSlug"
          :item-type="itemType"
          :num-displayed-replies="numDisplayedReplies"
          :user-avatar-url="userAvatarUrl"
          :user-name="userName"
          @inner-comment-removed="commentRemoved"
          @remove-comment-group="removeCommentGroup"
        />
      </div>

      <div
        v-if="displayEmptyPanel && isInitialized && !comments.length"
        class="no-comments-panel text-center padding-top-4em padding-bottom-4em"
      >
        <img
          class="space-bottom-2em"
          :src="noCommentsYetImgSrc"
        >
        <h4 class="h2">
          Be the first to leave a comment!
        </h4>
        <p class="large">
          Share your thoughts with the community
        </p>
      </div>
    </div>

    <DuiLoadingIndicator
      v-if="isLoading"
      class="comments-panel-loading"
      size="lg"
    />

    <div
      id="load-more-comments"
      class="load-more-comments"
    />
  </div>
</template>

<script>
import Vue from 'vue';

import commentsService from '@d-services/comments-service/comments-service';
import DCommentBox from './d-comment-box/d-comment-box.vue';
import DCommentBoxGuest from './d-comment-box/d-comment-box-guest.vue';
import DCommentsGroup from './d-comments-group/d-comments-group.vue';
import { getErrorMessage } from '@d-shared/utils/errors';
import { whenElementIsVisible } from '@dui/helpers/util-funcs';

export default {
  components: {
    DCommentBox,
    DCommentBoxGuest,
    DCommentsGroup,
  },
  props: {
    displayEmptyPanel: {
      type: Boolean,
      default: true,
    },
    displayHeading: {
      type: Boolean,
      default: true,
    },
    itemSlug: {
      type: String,
      required: true,
    },
    itemType: {
      type: String,
      validator: function (value) {
        return [
          'book',
          'post',
          'user-review',
        ].indexOf(value) !== -1;
      },
      required: true,
    },
    numDisplayedReplies: {
      type: Number,
      default: 1,
    },
    userAvatarUrl: {
      type: String,
      default: null,
    },
    userName: {
      type: String,
      default: null,
    },
  },
  data: () => ({
    comments: [],
    disableLoadMore: null,
    isInitialized: false,
    isLoading: false,
    isUserSignedIn: false,
    noCommentsYetImgSrc: require('@d-assets/no-comment.svg'),
    numComments: 0,
    paginationInfo: {
      hasMoreResults: false,
      pageNum: 0,
    },
  }),
  beforeDestroy() {
    if (this.disableLoadMore) {
      this.disableLoadMore();
    }
  },
  mounted() {
    this.loadComments();
    this.setIsUserSignedIn();
  },
  methods: {
    commentAdded(comment) {
      this.comments.push(comment);
      this.setNumComments(this.numComments + 1);
    },

    commentRemoved() {
      this.setNumComments(this.numComments - 1);
    },

    enableLoadMore() {
      if (this.disableLoadMore) {
        this.disableLoadMore();
      }

      const disableLoadMore = whenElementIsVisible('#load-more-comments', this.loadComments.bind(this));
      this.$set(this, 'disableLoadMore', disableLoadMore);
    },

    async loadComments() {
      this.$set(this, 'isLoading', true);

      try {
        this.paginationInfo.pageNum++;

        let commentsResult;

        switch(this.itemType) {
          case 'book':
            commentsResult = await commentsService.getBookComments(this.itemSlug, this.paginationInfo.pageNum);
            break;
          case 'post':
            commentsResult = await commentsService.getPostComments(this.itemSlug, this.paginationInfo.pageNum);
            break;
          case 'user-review':
            commentsResult = await commentsService.getUserReviewComments(this.itemSlug, this.paginationInfo.pageNum);
            break;
        }

        this.$set(this, 'comments', this.comments.concat(commentsResult.comments));
        this.$set(this.paginationInfo, 'hasMoreResults', !!commentsResult.meta.next_page);

        if (this.paginationInfo.hasMoreResults) {
          this.enableLoadMore();
        }

        if (!this.isInitialized) {
          this.$set(this, 'isInitialized', true);
          this.setNumComments(commentsResult.meta.total_count);
        }
      } catch (error) {
        Vue.duiNotificationManager.error(getErrorMessage(error));
      }

      this.$set(this, 'isLoading', false);
    },

    removeCommentGroup(removedComment) {
      const commentIdx = this.comments.findIndex(c => c.id === removedComment.id);
      if (commentIdx === -1) {
        return;
      }

      this.comments.splice(commentIdx, 1);
      this.commentRemoved();
    },

    setIsUserSignedIn() {
      this.$set(this, 'isUserSignedIn', !!this.userAvatarUrl);
    },

    setNumComments(numComments) {
      this.$set(this, 'numComments', numComments);
      this.$emit('num-comments-updated', numComments);
    },
  },
};
</script>

<style lang="scss">
@import "@dui-styles/base";

.d-comments-panel {
  position: relative;

  .load-more-comments {
    bottom: 0;
    position: absolute;
  }

  .dui-loading-indicator.comments-panel-loading {
    padding: $_4em 0;
    text-align: center;
    width: 100%;
  }

  .comments-container {
    border-top: $border-width-base solid getColor($grey);
    margin-top: ($_2em - $border-width-base);
  }

  .comment-item {
    .avatar {
      margin-right: $_1em;

      img {
        border: $border-width-base solid getColor($white);
        border-radius: 50%;
        height: $_4em;
        margin-top: $border-width-base;
        width: $_4em;
      }
    }
  }

  .no-comments-panel img {
    max-height: 230px;
    max-width: 75%;
  }
}
</style>
